import { CopyOutlined, MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Modal, Space, Typography, message as toast } from 'antd';
import { useEffect, useState } from 'react';
import { normalizePhone, smsUrl, whatsAppUrl } from '../../utils/quickShare';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Recipient's mobile as stored, e.g. "0300-1234567". */
  phone: string | null | undefined;
  recipientName: string;
  /** Pre-filled text — editable before sending. */
  defaultMessage: string;
  title?: string;
}

/**
 * Sends a message through the admin's own WhatsApp or SMS app rather than through a paid API.
 *
 * No Meta account, no access token, no template approval, no per-message cost. The catch is that
 * it is not automatic: this opens the app with the text ready and a person presses Send. That is
 * the price of needing no setup at all.
 */
export default function QuickShareModal({
  open,
  onClose,
  phone,
  recipientName,
  defaultMessage,
  title = 'Send message',
}: Props) {
  const [text, setText] = useState(defaultMessage);

  // Re-seed each time it opens, so a previous edit doesn't leak into the next recipient.
  useEffect(() => {
    if (open) setText(defaultMessage);
  }, [open, defaultMessage]);

  const normalized = normalizePhone(phone);

  const openWhatsApp = () => {
    window.open(whatsAppUrl(normalized, text), '_blank', 'noopener');
    onClose();
  };

  // Same tab on purpose: sms: is a protocol handler, not a page — a new tab would be left blank.
  const openSms = () => {
    if (!normalized) return;
    window.location.href = smsUrl(normalized, text);
    onClose();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Message copied.');
    } catch {
      toast.error('Could not copy — select the text and copy it manually.');
    }
  };

  return (
    <Modal maskClosable={false} title={title} open={open} onCancel={onClose} footer={null} width={520} destroyOnClose>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        To <strong>{recipientName}</strong>
        {phone ? ` · ${phone}` : ''}
      </Typography.Paragraph>

      {!normalized && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            phone
              ? 'This mobile number could not be read, so WhatsApp will open without a recipient and SMS is unavailable.'
              : 'No mobile number on file, so WhatsApp will open without a recipient and SMS is unavailable.'
          }
        />
      )}

      <Input.TextArea rows={10} value={text} onChange={(e) => setText(e.target.value)} />

      <Space style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }} wrap>
        <Button icon={<CopyOutlined />} onClick={copy}>
          Copy
        </Button>
        <Button icon={<MessageOutlined />} disabled={!normalized || !text.trim()} onClick={openSms}>
          Send via SMS
        </Button>
        <Button
          type="primary"
          icon={<WhatsAppOutlined />}
          disabled={!text.trim()}
          onClick={openWhatsApp}
          style={{ background: '#25D366', borderColor: '#25D366' }}
        >
          Open in WhatsApp
        </Button>
      </Space>
    </Modal>
  );
}
